import React, { useState } from "react";
import {
  ExternalLink, Search, Plus, Sparkles, Eye, ShieldAlert,
  ArrowRight, Globe, FileText, Zap, X, Trash2
} from "lucide-react";
import { SeoMarket, CompetitorItem, RadarData } from "./types";
import CompetitorRadarPanel from "./CompetitorRadarPanel";

interface SeoCompetitorsTabProps {
  market: SeoMarket;
  competitorsList: CompetitorItem[];
  setCompetitorsList: React.Dispatch<React.SetStateAction<CompetitorItem[]>>;
  radar?: RadarData | null;
}

export default function SeoCompetitorsTab({
  market,
  competitorsList,
  setCompetitorsList,
  radar = null
}: SeoCompetitorsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);

  // New Competitor Form
  const [newName, setNewName] = useState("");
  const [newDomain, setNewDomain] = useState("");
  const [newCategory, setNewCategory] = useState<CompetitorItem["category"]>("broker");
  const [newStrengths, setNewStrengths] = useState("");
  const [newHook, setNewHook] = useState("");

  const marketCompetitors = competitorsList.filter(c => c.market === market);
  const filteredList = marketCompetitors.filter(c =>
    !searchQuery ||
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.domain.toLowerCase().includes(searchQuery.toLowerCase())
  );

  // Build Meta Ad Library direct search link
  const getMetaLibraryUrl = (query: string, country: SeoMarket) => {
    const cc = country === "NO" ? "NO" : country === "DK" ? "DK" : country === "FI" ? "FI" : "SE";
    return `https://www.facebook.com/ads/library/?active_status=all&ad_type=all&country=${cc}&q=${encodeURIComponent(query)}&search_type=keyword_unordered&media_type=all`;
  };

  // Build Google Ads Transparency Center search link
  const getGoogleAdsTransparencyUrl = (domain: string) => {
    return `https://adstransparency.google.com/?region=anywhere&domain=${encodeURIComponent(domain)}`;
  };

  const handleAddCompetitor = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newName.trim() || !newDomain.trim()) return;

    const cleanDomain = newDomain.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");

    const newItem: CompetitorItem = {
      id: `comp-${Date.now()}`,
      market,
      name: newName.trim(),
      domain: cleanDomain,
      category: newCategory,
      metaLibrarySearch: newName.trim(),
      googleAdsSearch: cleanDomain,
      topLandingPages: [
        { title: "Forside / Låneberegner", path: `https://${cleanDomain}` }
      ],
      keyStrengths: newStrengths.trim() || "Aktiv på søgemaskiner og betalte medier.",
      observedAdHooks: newHook.trim() ? [newHook.trim()] : []
    };

    setCompetitorsList(prev => [newItem, ...prev]);
    setIsAddModalOpen(false);

    // Reset
    setNewName("");
    setNewDomain("");
    setNewStrengths("");
    setNewHook("");
  };

  const handleDelete = (id: string) => {
    if (window.confirm("Vil du slette denne konkurrent fra overvågning?")) {
      setCompetitorsList(prev => prev.filter(c => c.id !== id));
    }
  };

  return (
    <div className="space-y-6">

      {radar && <CompetitorRadarPanel market={market} radar={radar} />}

      {/* Media Buying & SEO Dual-Use Header */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm space-y-4">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-gray-100 pb-4">
          <div>
            <div className="flex items-center gap-2">
              <span className="text-xl">🕵️</span>
              <h3 className="font-bold text-gray-900 text-base">
                Konkurrent- & Annonceovervågning (SEO + Media Buying)
              </h3>
              <span className="px-2 py-0.5 rounded text-[11px] font-bold bg-purple-100 text-purple-800">
                100% Gratis Ad Spionage
              </span>
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Spionér direkte på de største låneformidlere i {market === "SE" ? "Sverige" : "Norge"} (Lendo, Sambla, Zmarta). Se deres aktive Facebook/Instagram annoncer, hooks, og landing pages for at optimere både vores egne artikler og fremtidige paid ads.
            </p>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              onClick={() => setIsAddModalOpen(true)}
              className="flex items-center gap-1.5 px-3.5 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white text-xs font-bold rounded-xl transition shadow-xs"
            >
              <Plus className="w-4 h-4" />
              <span>Tilføj konkurrent</span>
            </button>
          </div>
        </div>

        {/* 2 One-Click Global Free Spying Engines */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs">
          <a
            href={getMetaLibraryUrl("lån", market)}
            target="_blank"
            rel="noreferrer"
            className="p-4 rounded-xl bg-blue-50/60 border border-blue-200/80 hover:bg-blue-50 transition flex items-center justify-between group"
          >
            <div className="space-y-1">
              <div className="font-bold text-blue-900 flex items-center gap-1.5">
                <span>📘 Meta Ad Library ({market})</span>
              </div>
              <p className="text-blue-700 text-[11px]">
                Søg alle aktive Facebook & Instagram annoncer på søgeordet &quot;lån&quot; i {market}.
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-blue-500 group-hover:translate-x-0.5 transition" />
          </a>

          <a
            href={`https://adstransparency.google.com/?region=${market}`}
            target="_blank"
            rel="noreferrer"
            className="p-4 rounded-xl bg-emerald-50/60 border border-emerald-200/80 hover:bg-emerald-50 transition flex items-center justify-between group"
          >
            <div className="space-y-1">
              <div className="font-bold text-emerald-900 flex items-center gap-1.5">
                <span>🔍 Google Ads Transparency Center</span>
              </div>
              <p className="text-emerald-700 text-[11px]">
                Se aktive Google Search, YouTube og Display-annoncer fra svenske og norske lånegiganter.
              </p>
            </div>
            <ExternalLink className="w-4 h-4 text-emerald-500 group-hover:translate-x-0.5 transition" />
          </a>
        </div>
      </div>

      {/* Competitors List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {filteredList.map((comp) => (
          <div key={comp.id} className="bg-white rounded-2xl border border-gray-100 p-6 shadow-sm space-y-4">
            
            {/* Header */}
            <div className="flex items-start justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h4 className="font-bold text-gray-900 text-base">{comp.name}</h4>
                  <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider bg-gray-100 text-gray-700">
                    {comp.category}
                  </span>
                </div>
                <a
                  href={`https://${comp.domain}`}
                  target="_blank"
                  rel="noreferrer"
                  className="font-mono text-xs text-[#0F3F34] hover:underline flex items-center gap-1 mt-0.5"
                >
                  <span>{comp.domain}</span>
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>

              <button
                onClick={() => handleDelete(comp.id)}
                className="p-1.5 text-gray-300 hover:text-red-500 rounded-lg transition"
                title="Slet"
              >
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>

            {/* Strengths & Overview */}
            <p className="text-xs text-gray-600 bg-gray-50 p-3 rounded-xl border border-gray-100">
              {comp.keyStrengths}
            </p>

            {/* Observed Ad Hooks (Media Buying Swipe File) */}
            {comp.observedAdHooks && comp.observedAdHooks.length > 0 && (
              <div className="space-y-1.5">
                <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                  Observerede Annonce-Hooks (Swipe File)
                </span>
                <div className="space-y-1">
                  {comp.observedAdHooks.map((hook, idx) => (
                    <div key={idx} className="p-2 bg-amber-50/70 rounded-lg border border-amber-200/60 text-xs text-amber-900 font-medium">
                      &quot;{hook}&quot;
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Top Landing Pages */}
            <div className="space-y-1.5">
              <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">
                Vigtigste Landingssider (SEO & Paid Funnel)
              </span>
              <div className="space-y-1 text-xs">
                {comp.topLandingPages.map((lp, idx) => (
                  <a
                    key={idx}
                    href={lp.path}
                    target="_blank"
                    rel="noreferrer"
                    className="flex items-center justify-between p-2 rounded-lg bg-gray-50 hover:bg-gray-100 transition text-gray-700"
                  >
                    <span>{lp.title}</span>
                    <ExternalLink className="w-3.5 h-3.5 text-gray-400" />
                  </a>
                ))}
              </div>
            </div>

            {/* Direct Free Spy Actions */}
            <div className="pt-3 border-t border-gray-100 grid grid-cols-2 gap-2 text-xs">
              <a
                href={getMetaLibraryUrl(comp.metaLibrarySearch, market)}
                target="_blank"
                rel="noreferrer"
                className="py-2 px-3 bg-blue-50 hover:bg-blue-100 text-blue-800 font-bold rounded-xl transition text-center flex items-center justify-center gap-1.5"
              >
                <span>Meta Ads</span>
                <ExternalLink className="w-3 h-3" />
              </a>

              <a
                href={getGoogleAdsTransparencyUrl(comp.googleAdsSearch)}
                target="_blank"
                rel="noreferrer"
                className="py-2 px-3 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 font-bold rounded-xl transition text-center flex items-center justify-center gap-1.5"
              >
                <span>Google Ads</span>
                <ExternalLink className="w-3 h-3" />
              </a>
            </div>

          </div>
        ))}
      </div>

      {/* Modal: Tilføj Konkurrent */}
      {isAddModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-xs flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 space-y-4 shadow-xl border border-gray-100">
            <div className="flex items-center justify-between border-b border-gray-100 pb-3">
              <h3 className="font-bold text-gray-900 text-base flex items-center gap-2">
                <Plus className="w-4 h-4 text-[#0F3F34]" />
                <span>Overvåg ny Konkurrent ({market})</span>
              </h3>
              <button onClick={() => setIsAddModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                <X className="w-4 h-4" />
              </button>
            </div>

            <form onSubmit={handleAddCompetitor} className="space-y-3.5 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Navn *</label>
                <input
                  type="text"
                  required
                  placeholder="f.eks. Enklare"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Domæne *</label>
                <input
                  type="text"
                  required
                  placeholder="enklare.se"
                  value={newDomain}
                  onChange={(e) => setNewDomain(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg font-mono text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Kategori</label>
                <select
                  value={newCategory}
                  onChange={(e) => setNewCategory(e.target.value as CompetitorItem["category"])}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg"
                >
                  <option value="broker">Låneformidler / Broker</option>
                  <option value="bank">Bank / Direkte Långiver</option>
                  <option value="affiliate">Affiliate / Sammenligningsside</option>
                </select>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Nøglestyrker / Profil</label>
                <textarea
                  rows={2}
                  placeholder="Hvad gør de godt i SEO eller Media Buying?"
                  value={newStrengths}
                  onChange={(e) => setNewStrengths(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Observeret Annonce Hook (Valgfri)</label>
                <input
                  type="text"
                  placeholder="f.eks. Sänk räntan på 2 minuter utan avgift"
                  value={newHook}
                  onChange={(e) => setNewHook(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-xs"
                />
              </div>

              <div className="pt-3 border-t border-gray-100 flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setIsAddModalOpen(false)}
                  className="px-4 py-2 border border-gray-200 rounded-xl text-gray-600 font-bold hover:bg-gray-50 transition"
                >
                  Annuller
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-[#0F3F34] hover:bg-[#165A4B] text-white font-bold rounded-xl transition"
                >
                  Gem konkurrent
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

    </div>
  );
}
